import router from "@/router";
import mutationTypes from "@/vuex/mutation-types";
import actionTypes from "@/vuex/action-types";
import GeneralApiService from "@/services/api/generalApiService";
import SecureApiService from "@/services/api/secureApiService";
import * as constants from "@/common/constants";
import SecureModel from "@/models/secureModel";

const generalApiService = new GeneralApiService();
const secureApiService = new SecureApiService();
const secureModel = new SecureModel();

const getDefaultState = () => {
  return {
    status: "",
    access_token: secureModel.getAccessToken(),
    secureLoginError: "",
    secureLogoutError: "",

    secureRestoreLinkStatus: "",
    secureRestoreLinkError: "",

    secureRestoreResetStatus: "",
    secureRestoreResetErrors: {}
  };
};

const initialState = getDefaultState();

const mutations = {
  [mutationTypes.RESET_STATE](state) {
    Object.assign(state, getDefaultState());
  },
  [mutationTypes.AUTH_REQUEST](state) {
    state.status = constants.STATUS_LOADING;
    state.secureLoginError = "";
  },
  [mutationTypes.AUTH_SUCCESS](state, { access_token }) {
    state.status = constants.STATUS_SUCCESS;
    state.access_token = access_token;
  },
  [mutationTypes.AUTH_ERROR](state, error) {
    state.status = constants.STATUS_ERROR;
    state.secureLoginError = error;
  },

  [mutationTypes.LOGOUT_REQUEST](state) {
    state.status = constants.STATUS_LOADING;
    state.secureLogoutError = "";
  },
  [mutationTypes.LOGOUT_ERROR](state, error) {
    state.status = constants.STATUS_ERROR;
    state.secureLogoutError = error;
  },

  [mutationTypes.PASSWORD_RESTORE_LINK_REQUEST](state) {
    state.secureRestoreLinkStatus = constants.STATUS_LOADING;
    state.secureRestoreLinkError = "";
  },
  [mutationTypes.PASSWORD_RESTORE_LINK_SUCCESS](state) {
    state.secureRestoreLinkStatus = constants.STATUS_SUCCESS;
  },
  [mutationTypes.PASSWORD_RESTORE_LINK_ERROR](state, error) {
    state.secureRestoreLinkStatus = constants.STATUS_ERROR;
    state.secureRestoreLinkError = error;
  },

  [mutationTypes.PASSWORD_RESTORE_RESET_REQUEST](state) {
    state.secureRestoreResetStatus = constants.STATUS_LOADING;
    state.secureRestoreResetErrors = {};
  },
  [mutationTypes.PASSWORD_RESTORE_RESET_SUCCESS](state) {
    state.secureRestoreResetStatus = constants.STATUS_SUCCESS;
  },
  [mutationTypes.PASSWORD_RESTORE_RESET_ERROR](state, errors) {
    state.secureRestoreResetStatus = constants.STATUS_ERROR;
    state.secureRestoreResetErrors = errors;
  }
};

const getters = {
  isLoggedIn: state => !!state.access_token,
  secureStatus: state => state.status,
  secureAccessToken: state => state.access_token,
  secureLoginError: state => state.secureLoginError,

  secureRestoreLinkStatus: state => state.secureRestoreLinkStatus,
  secureRestoreLinkError: state => state.secureRestoreLinkError,

  secureRestoreResetStatus: state => state.secureRestoreResetStatus,
  secureRestoreResetErrors: state => state.secureRestoreResetErrors
};

const actions = {
  [actionTypes.LOGIN]({ commit, dispatch }, user) {
    commit(mutationTypes.AUTH_REQUEST);
    return secureApiService
      .login(user)
      .then(secureData => {
        secureModel.setSecureData({
          accessToken: secureData.access_token,
          refreshToken: secureData.refresh_token
        });
        commit(mutationTypes.AUTH_SUCCESS, { ...secureData });

        location.href = process.env.VUE_APP_ENV_LOGIN_URL;
      })
      .catch(() => {
        commit(mutationTypes.AUTH_ERROR, constants.ERROR_LOGIN_ERROR);
      });
  },
  [actionTypes.LOGOUT]({ commit }) {
    commit(mutationTypes.LOGOUT_REQUEST);
    return secureApiService
      .logout()
      .then(() => {
        secureModel.setSecureData({});
        commit(mutationTypes.RESET_STATE);
      })
      .catch(err => {
        commit(mutationTypes.LOGOUT_ERROR, err);
      });
  },

  [actionTypes.OPEN_FORGOT_PASSWORD]({ commit }) {
    commit(mutationTypes.RESET_STATE);
    router.push("/forgot-password");
  },
  [actionTypes.OPEN_LOGIN]({ commit }) {
    commit(mutationTypes.RESET_STATE);
    router.push("/login");
  },

  [actionTypes.PASSWORD_RESTORE_LINK]({ commit }, payload) {
    commit(mutationTypes.PASSWORD_RESTORE_LINK_REQUEST);
    return secureApiService
      .postPasswordRestoreLink(payload)
      .then(() => {
        commit(mutationTypes.PASSWORD_RESTORE_LINK_SUCCESS);
      })
      .catch(err => {
        commit(mutationTypes.PASSWORD_RESTORE_LINK_ERROR, err.message);
      });
  },

  [actionTypes.PASSWORD_RESTORE_RESET]({ commit }, payload) {
    commit(mutationTypes.PASSWORD_RESTORE_RESET_REQUEST);
    return secureApiService
      .postPasswordRestoreReset(payload)
      .then(() => {
        commit(mutationTypes.PASSWORD_RESTORE_RESET_SUCCESS);
      })
      .catch(err => {
        commit(mutationTypes.PASSWORD_RESTORE_RESET_ERROR, err);
      });
  }
};

export default {
  state: initialState,
  getters,
  actions,
  mutations
};
